import { observable, action } from 'mobx';
import { eventViewerApi } from '../api/event-viewer';
import { dialogEvents } from '../component';

export class EventViewerStore {
  @observable eventRecords: EventRecord[];
  @observable selectedRecord: EventRecord;

  @observable currentPage: number = 1;
  @observable totalRecordCount: number = 0;
  @observable recordsPerPage: number = 50;
  @observable
  logLevels: Array<EventLogLevel> = [
    'Critical',
    'Error',
    'Warning',
    'Information',
    'Verbose',
  ];
  @observable selectedLevels: Array<EventLogLevel> = [];
  @observable logNames: Array<string> = [];
  @observable selectedLogNames: Array<string> = [];
  @observable serviceId: string;
  @observable searchString: string = '';

  @observable isDateTimeFilterValid: boolean = true;
  @observable fromDate: string = '';
  @observable fromTime: string = '';
  @observable toDate: string = '';
  @observable toTime: string = '';
  @observable sortColumn: EventViewerSortColumn = 'TimeCreated';
  @observable sortDirection: EventViewerSortDirection = 'Descending';

  @observable isFilterSidebarOpen: boolean;
  @observable isFilterApplied: boolean;

  pageSetterTimeout: NodeJS.Timeout;

  get totalPages(): number {
    return Math.ceil(this.totalRecordCount / this.recordsPerPage);
  }

  @action
  clearFilter() {
    this.selectedLevels.splice(0);
    this.selectedLogNames.splice(0);
    this.fromDate = '';
    this.fromTime = '';
    this.toDate = '';
    this.toTime = '';
    this.isDateTimeFilterValid = true;
    this.queryEventViewer();
  }

  @action
  initData() {
    eventViewerApi.getLogNames(this.serviceId);
    this.queryEventViewer();
  }

  @action.bound
  pageUp() {
    if (this.currentPage < this.totalRecordCount / this.recordsPerPage) {
      this.currentPage++;
      this.queryEventViewer();
    }
  }

  @action.bound
  pageDown() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.queryEventViewer();
    }
  }

  @action
  queryEventViewer() {
    if (!this.isDateTimeFilterValid) {
      dialogEvents.dialog({
        body: 'Date/time filter options are invalid.',
        title: 'Invalid Filter',
      });
      return;
    }

    this.eventRecords = null;
    var query = {
      currentPage: this.currentPage,
      fromDate: this.fromDate,
      fromTime: this.fromTime,
      levels: this.selectedLevels,
      logNames: this.selectedLogNames,
      recordsPerPage: this.recordsPerPage,
      searchString: this.searchString,
      serviceId: this.serviceId,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      toDate: this.toDate,
      toTime: this.toTime,
    } as EventQuery;
    this.isFilterApplied = this.checkFilterApplied();
    eventViewerApi.sendEventViewerQuery(query);
  }

  @action.bound
  setFromDate(fromDate: string) {
    this.fromDate = fromDate;

    if (!this.fromTime) {
      this.fromTime = '00:00';
    }

    this.isDateTimeFilterValid = this.checkDateTimeFilter();
  }

  @action.bound
  setFromTime(fromTime: string) {
    this.fromTime = fromTime;
    this.isDateTimeFilterValid = this.checkDateTimeFilter();
  }

  @action
  setLogNames(logNames: string[]) {
    this.logNames = logNames;
  }

  @action
  setPage(ev: React.ChangeEvent<HTMLInputElement>) {
    clearTimeout(this.pageSetterTimeout);
    ev.persist();

    var newValue = Number(ev.target.value);

    if (isNaN(newValue)) {
      ev.preventDefault();
      return;
    }

    if (
      newValue < 1 ||
      newValue > Math.ceil(this.totalRecordCount / this.recordsPerPage)
    ) {
      ev.preventDefault();
      return;
    }

    this.currentPage = newValue;

    this.pageSetterTimeout = setTimeout(() => {
      ev.target.blur();
      this.queryEventViewer();
    }, 1500);
  }

  @action
  setQueryResult(result: EventViewerQueryResult) {
    this.serviceId = result.serviceId;
    this.eventRecords = result.eventRecords;
    this.totalRecordCount = result.totalRecords;

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
      this.queryEventViewer();
    } else if (this.currentPage == 0) {
      this.currentPage = 1;
    }
  }

  @action
  setSearchString(searchString: string) {
    this.searchString = searchString;
  }

  @action
  setSelectedRecord(eventRecord: EventRecord) {
    this.selectedRecord = eventRecord;
  }

  @action.bound
  setFilterSidebarIsOpen(isOpen: boolean) {
    this.isFilterSidebarOpen = isOpen;
  }

  @action
  setSortOptions(
    direction: EventViewerSortDirection,
    column: EventViewerSortColumn,
  ) {
    this.sortDirection = direction;
    this.sortColumn = column;
    this.queryEventViewer();
  }

  @action.bound
  setToDate(toDate: string) {
    this.toDate = toDate;

    if (!this.toTime) {
      this.toTime = '23:59';
    }

    this.isDateTimeFilterValid = this.checkDateTimeFilter();
  }

  @action.bound
  setToTime(toTime: string) {
    this.toTime = toTime;
    this.isDateTimeFilterValid = this.checkDateTimeFilter();
  }

  @action
  updateEvents(eventRecords: EventRecord[]) {
    this.eventRecords = eventRecords;
  }

  @action
  updateSelectedLogLevels(ev: Array<{ label: string; value: EventLogLevel }>) {
    if (ev) {
      this.selectedLevels = ev.map(x => x.value);
    } else {
      this.selectedLevels = [];
    }
  }

  @action
  updateSelectedLogNames(ev: Array<{ label: string; value: string }>) {
    if (ev) {
      this.selectedLogNames = ev.map(x => x.value);
    } else {
      this.selectedLogNames = [];
    }
  }

  checkDateTimeFilter(): boolean {
    if (!this.fromDate && !this.fromTime && !this.toDate && !this.toTime) {
      return true;
    }

    if (
      new Date(`${this.fromDate} ${this.fromTime}`) >=
      new Date(`${this.toDate} ${this.toTime}`)
    ) {
      return false;
    }

    if (this.fromDate && this.toDate && !this.fromTime && !this.toTime) {
      return true;
    }

    if (this.fromDate && this.fromTime && this.toDate && this.toTime) {
      return true;
    }

    return false;
  }

  checkFilterApplied(): boolean {
    try {
      return (
        this.selectedLevels.length > 0 ||
        this.selectedLogNames.length > 0 ||
        this.fromDate.length > 0 ||
        this.fromTime.length > 0 ||
        this.toDate.length > 0 ||
        this.toTime.length > 0
      );
    } catch {
      return false;
    }
  }
}

export const eventViewerStore = new EventViewerStore();
